import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  orderBy,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../config';
import type { SubPatternType } from '../../../../types/subPattern';
import { resultStatus } from '../../../../constants/enum';
import {
  convertItemOverridesToCamelCase,
  type ItemOverrideSnake,
} from '../../../../utils/pattern/subPatternConverter';

/**
 * Firestore ドキュメントを SubPatternType に変換する共通ヘルパー。
 * 欠損フィールドには既定値を入れ、古い形式のデータでも画面が壊れないようにする。
 */
const mapDocToSubPattern = (docId: string, data: Record<string, unknown>): SubPatternType => ({
  subPatternId: docId,
  sub_pattern_name: (data.sub_pattern_name as string) ?? '',
  description: (data.description as string) ?? '',
  item_overrides: convertItemOverridesToCamelCase(data.item_overrides as ItemOverrideSnake[]),
  // 欠損は有効として扱う（is_classification_target と同じ流儀）
  is_active: (data.is_active as boolean | undefined) !== false,
  display_order: (data.display_order as number) ?? 0,
  source_result_id: (data.source_result_id as string | null) ?? null,
  created_at: data.created_at as Timestamp,
  updated_at: data.updated_at as Timestamp,
  created_by: (data.created_by as string) ?? '',
  updated_by: (data.updated_by as string) ?? '',
  // 代理ログイン中の操作か（#3314）。一覧の作成者を「事務局」と出すかの判定に使う。
  // 導入前のデータには無いため、欠損は通常操作として扱う
  is_proxy: (data.is_proxy as boolean | undefined) ?? false,
  operator_id: data.operator_id as string | undefined,
});

/**
 * 指定パターンの子パターンを表示順で全件取得する（#2967）。
 * 無効な子パターン（is_active=false）も含めて返す。設定画面で編集できる必要があるため。
 */
export const getSubPatterns = async (patternId: string): Promise<SubPatternType[]> => {
  const subPatternsRef = collection(db, 'patterns', patternId, 'sub_patterns');
  const q = query(subPatternsRef, orderBy('display_order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => mapDocToSubPattern(d.id, d.data()));
};

/** 子パターンを1件取得する。存在しなければ null。 */
export const getSubPattern = async (
  patternId: string,
  subPatternId: string,
): Promise<SubPatternType | null> => {
  const variantRef = doc(db, 'patterns', patternId, 'sub_patterns', subPatternId);
  const snapshot = await getDoc(variantRef);
  if (!snapshot.exists()) return null;
  return mapDocToSubPattern(snapshot.id, snapshot.data());
};

/**
 * 指定パターンの子パターンの件数。上限チェックに使う。
 * 無効な子パターンも件数に含める（設定として存在している以上、枠を消費しているとみなす）。
 */
export const getSubPatternCount = async (patternId: string): Promise<number> => {
  const subPatternsRef = collection(db, 'patterns', patternId, 'sub_patterns');
  const snapshot = await getDocs(subPatternsRef);
  return snapshot.size;
};

/**
 * 見本を探すときに読む結果の上限。子パターンが使われていれば先頭に十分な候補が入る。
 * 全件走査すると結果数に比例して遅くなるため上限を設ける
 */
const MAX_SAMPLE_CANDIDATES = 20;

/**
 * 子パターンの取得ルールを編集するとき、見本にする結果IDを決める（#2967）。
 *
 * 取得ルールは「帳票のどこから取るか」を書くものなので、**帳票を見ないと編集できない**。
 * そのため編集は結果画面へ遷移して行う。その遷移先をここで決める。
 *
 * 1. 作成のきっかけになった結果（source_result_id）を使う
 * 2. その結果が削除済み・欠損なら、**同じ差分で読み取った最新の結果**を使う
 * 3. どちらも無ければ null（呼び出し側でメッセージを出す）
 *
 * 2 のフォールバックが要るのは、見本の結果が消されても差分自体は残るため。
 * 子パターンは読取パターンの設定であり、個々の結果より寿命が長い。
 */
export const resolveSubPatternSampleResultId = async (
  patternId: string,
  subPatternId: string,
  sourceResultId: string | null,
): Promise<string | null> => {
  // 1. 作成のきっかけになった結果。生きていればこれが最も適切
  if (sourceResultId) {
    try {
      const snapshot = await getDoc(doc(db, 'results', sourceResultId));
      const data = snapshot.data();
      if (data && data.status === resultStatus.valid) return sourceResultId;
    } catch {
      // 参照できない場合はフォールバックへ進む
    }
  }

  // 2. 同じ差分で読み取った最新の結果。
  //    pattern_id で絞ってから子パターンIDで絞り込む（複合インデックスを増やさないため、
  //    並び替えはクライアント側で行う）
  try {
    const snapshot = await getDocs(
      query(
        collection(db, 'results'),
        where('pattern_id', '==', patternId),
        where('sub_pattern_id', '==', subPatternId),
        limit(MAX_SAMPLE_CANDIDATES),
      ),
    );
    const candidates = snapshot.docs
      .filter(d => d.data()?.status === resultStatus.valid)
      .sort((a, b) => {
        const aTime = (a.data()?.created_at as Timestamp | undefined)?.toMillis() ?? 0;
        const bTime = (b.data()?.created_at as Timestamp | undefined)?.toMillis() ?? 0;
        return bTime - aTime;
      });
    return candidates[0]?.id ?? null;
  } catch {
    return null;
  }
};
